import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  Container, 
  List, 
  ListItem, 
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  History as HistoryIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom'; 
import { withdrawRecord, queryInviteInfo } from '../api/modules/payment';
import { showMessage } from '../utils/index';
import TopBar from '../components/TopBar';
import { handleCopyLink } from '../utils/share';
import { removeToken } from '../utils/auth';
// 自定义样式组件
const BalanceCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#000000',
  color: '#ffffff',
  borderRadius: '8px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  borderRadius: '24px',
  padding: theme.spacing(1, 2.5),
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: active ? '#000000' : '#f5f5f7',
  color: active ? '#ffffff' : '#6B7280',
  '&:hover': {
    backgroundColor: active ? '#000000' : '#e8e8ed',
  },
  marginRight: theme.spacing(1),
  whiteSpace: 'nowrap',
  minWidth: 'auto',
  fontSize: '14px',
}));

const CashOutButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFCC00',
  color: '#000000',
  borderRadius: '8px',
  padding: theme.spacing(1, 3),
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#e6b800',
  },
  textTransform: 'none',
  fontSize: '14px',
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  '-ms-overflow-style': 'none', // IE
  padding: theme.spacing(1, 0),
  position: 'relative',
}));

const TransactionHistoryButton = styled(Button)(({ theme }) => ({
  color: '#007AFF',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '16px',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// 添加空状态组件
const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  marginTop: theme.spacing(4),
}));

// 添加状态颜色映射
const statusColors = {
  completed: '#10B981', // 绿色
  pending: '#6B7280',   // 灰色
  failed: '#EF4444',    // 红色
  mailed: '#F59E0B',    // 橙色
  delivered: '#10B981'  // 绿色
};

// 主组件
const WithdrawalRecords = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const scrollContainerRef = useRef(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  useEffect(() => {
    fetchInviteInfo();
  }, []);

  // 获取提现记录
  useEffect(() => {
    fetchWithdrawRecords();
  }, [activeFilter]);

  const fetchInviteInfo = () => {
    queryInviteInfo({}, (rst) => {
      if (rst.code === 200) {
        setInviteInfo(rst.data);
      }
    });
  };

  const fetchWithdrawRecords = () => {
    const params = {
      pageSize: 100,
      pageNum: 1,
      ...(getFilterType(activeFilter) && { type: getFilterType(activeFilter) })
    };

    withdrawRecord(params, (rst) => {
      const { code, rows } = rst;
      if (code === 200 && rows?.length) {
        const formattedTransactions = rows.map(item => ({
          id: item.withdrawId,
          date: formatDate(item.createTime),
          type: getTransactionType(item.type),
          title: getTransactionTitle(item.type),
          details: getTransactionDetails(item),
          status: getTransactionStatus(item.status),
          amount: `$${item.price}`
        }));
        setTransactions(formattedTransactions);
      }
    });
  };

  // 转换筛选类型为接口参数
  const getFilterType = (filter) => {
    switch (filter) {
      case 'ach':
        return 1; // 银行卡
      case 'echeck':
        return 2; // 电子支票
      case 'paper':
        return 3; // 纸质支票
      default:
        return 0; // 全部
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // 获取交易类型
  const getTransactionType = (type) => {
    switch (type) {
      case 1:
        return 'ach';
      case 2:
        return 'echeck';
      case 3:
        return 'paper';
      default:
        return 'ach';
    }
  };

  // 获取交易标题
  const getTransactionTitle = (type) => {
    switch (type) {
      case 1:
        return 'ACH Transfer';
      case 2:
        return 'eCheck';
      case 3:
        return 'Paper Check';
      default:
        return 'Transfer';
    }
  };

  // 获取交易详情
  const getTransactionDetails = (item) => {
    switch (item.type) {
      case 1:
        return `${item.bankName} ••••${item.accountNo.slice(-4)}`;
      case 2:
        return item.accountNo; // email
      case 3:
        return `USPS Mail • ${item.mailingAddress}`;
      default:
        return '';
    }
  };

  // 获取交易状态
  const getTransactionStatus = (status) => {
    switch (status) {
      case 11:
        return 'completed';
      case 0:
      case 10:
        return 'pending';
      case 20:
      case 21:
        return 'failed';
      default:
        return 'pending';
    }
  };

  // 检查滚动箭头显示
  const checkScrollArrows = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // 滚动处理
  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 150;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount 
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // 监听滚动容器变化
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      checkScrollArrows();
      scrollContainer.addEventListener('scroll', checkScrollArrows);
      
      // 初始检查是否需要显示右箭头
      if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
        setShowRightArrow(true);
      }
      
      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollArrows);
      };
    }
  }, []);

  // 状态图标渲染
  const renderStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" style={{ color: '#10b981' }} />;
      case 'mailed':
        return <LocalShippingIcon fontSize="small" style={{ color: '#f59e0b' }} />;
      case 'delivered':
        return <CheckCircleIcon fontSize="small" style={{ color: '#10b981' }} />;
      default:
        return null;
    }
  };

  // 状态文本渲染 - 更新颜色逻辑
  const renderStatusText = (status) => {
    const text = {
      completed: 'Completed',
      mailed: 'Mailed',
      delivered: 'Delivered',
      pending: 'Pending',
      failed: 'Failed'
    }[status] || 'Pending';

    return (
      <Typography 
        variant="body2" 
        sx={{ 
          fontSize: '14px',
          color: statusColors[status],
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {renderStatusIcon(status)}
        {text}
      </Typography>
    );
  };

  const goCashOUt = () => {
    if (inviteInfo?.availableAmount > 0) {
      navigate('/campaigns/invitations/cash-out');
    } else {
      showMessage('No available balance', 'warning');
    }
  }

  return (
    <Box>
    <TopBar
        isLogin={true}
        onShareClick={() => navigate('/campaigns/invitations/referral')}
        onCouponClick={() => navigate('/campaigns/invitations/tax-declaration')}
        onAvatarClick={handleUserMenuOpen}
        userMenuAnchor={userMenuAnchor}
        handleCopyLink={handleCopyLink}
        onUserMenuClose={handleUserMenuClose}
        navigate={navigate}
        onLogout={() => {
          removeToken();
          showMessage("Logout successfully", "success");
          setTimeout(() => {
            navigate("/login");
          }, 1500);
          handleUserMenuClose();
        }}
      />
      <Container 
      maxWidth="sm" 
      sx={{ 
        py: 2, 
        mt: { xs: '56px', md: '64px' },
        px: { xs: 2, sm: 3 },
        backgroundColor: '#ffffff',
        minHeight: '100vh'
      }}
    >
      {/* 余额卡片 */}
      <BalanceCard>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="500" fontSize="18px">
            Available Balance
          </Typography>
          <CashOutButton onClick={() => goCashOUt()} variant="contained" disableElevation>
            Cash Out
          </CashOutButton>
        </Box>
        
        <Typography variant="h3" fontWeight="bold" mb={1} fontSize="42px">
          ${inviteInfo?.availableAmount || 0}
        </Typography>
        
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="rgba(255,255,255,0.7)" fontSize="16px">
            {transactions.length} transactions
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)" fontSize="16px">
            Last: {transactions[transactions.length - 1]?.date || 'No transactions'}
          </Typography>
        </Box>
      </BalanceCard>

      {/* 过滤器 */}
      <Box position="relative" mb={2} sx={{ mx: -1 }}>
        {showLeftArrow && (
          <IconButton 
            size="small"
            sx={{ 
              position: 'absolute', 
              left: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
              width: '28px',
              height: '28px'
            }}
            onClick={() => handleScroll('left')}
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
        )}
        
        <Box sx={{ px: 3 }}>
          <ScrollContainer ref={scrollContainerRef}>
            <FilterButton 
              active={activeFilter === 'all' ? 1 : 0} 
              onClick={() => setActiveFilter('all')}
            >
              All
            </FilterButton>
            {/* <FilterButton 
              active={activeFilter === 'ach' ? 1 : 0} 
              onClick={() => setActiveFilter('ach')}
            >
              ACH Transfer
            </FilterButton> */}
            <FilterButton 
              active={activeFilter === 'paper' ? 1 : 0} 
              onClick={() => setActiveFilter('paper')}
            >
              Paper Check
            </FilterButton>
            {/* <FilterButton 
              active={activeFilter === 'echeck' ? 1 : 0} 
              onClick={() => setActiveFilter('echeck')}
            >
              eCheck
            </FilterButton> */}
          </ScrollContainer>
        </Box>
        
        {showRightArrow && (
          <IconButton 
            size="small"
            sx={{ 
              position: 'absolute', 
              right: 0, 
              top: '50%', 
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
              width: '28px',
              height: '28px'
            }}
            onClick={() => handleScroll('right')}
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* 交易列表 */}
      {transactions.length > 0 ? (
        <>
          <List sx={{ p: 0 }}>
            {transactions.map((transaction, index) => (
              <React.Fragment key={transaction.id}>
                <Typography 
                  variant="body2" 
                  fontWeight="500" 
                  sx={{ 
                    mt: index > 0 ? 1 : 0, 
                    fontWeight: '500',
                    color: '#1D1D1F', 
                    fontSize: '15px' 
                  }}
                >
                  {transaction.date}
                </Typography>

                <ListItem 
                  sx={{ 
                    px: 0, 
                    pt: 0,
                    py: 2,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.01)' },
                    cursor: 'pointer',
                    borderRadius: '10px'
                  }}
                >
                  <Box width="100%">
                    {/* 标题和详情部分 */}
                    <Box>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="800" 
                        fontSize="16px"
                        color="#000"
                        mb={0.5}
                      >
                        {transaction.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#6B7280" 
                        fontSize="14px" 
                        mb={1}
                      >
                        {transaction.details}
                      </Typography>
                      {renderStatusText(transaction.status)}
                    </Box>

                    {/* 金额显示 - 移到右下角 */}
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        right: 0,
                        bottom: '16px',
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="900" 
                        fontSize="17px"
                        color="#1D1D1F"
                      >
                        {transaction.amount}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
                {index < transactions.length - 1 && (
                  <Divider sx={{ opacity: 0.6 }} />
                )}
              </React.Fragment>
            ))}
          </List>

          {/* 交易历史按钮 - 只在有交易记录时显示 */}
          <Box display="flex" justifyContent="center" mt={2}>
            <TransactionHistoryButton 
              startIcon={<HistoryIcon />}
              onClick={() => console.log('View transaction history')}
            >
              Transaction History
            </TransactionHistoryButton>
          </Box>
        </>
      ) : (
        <EmptyState>
          <Typography 
            variant="h6" 
            color="#1D1D1F" 
            fontWeight="500" 
            mb={1}
          >
            No Transactions Yet
          </Typography>
          <Typography 
            variant="body2" 
            color="#6B7280"
            sx={{ maxWidth: '280px' }}
          >
            Your transaction history will appear here once you start making withdrawals
          </Typography>
        </EmptyState>
      )}
    </Container>
      
    </Box>

  );
};

export default WithdrawalRecords;
